import { gql, useQuery } from '@apollo/client';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  type Theme,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { ComponentBox } from '@/admins/SmartReports/components/Components/Common/ComponentBox';
import { ComponentTitle } from '@/admins/SmartReports/components/Components/Common/ComponentTitle';
import { LoadingErrorWrapper } from '@/admins/SmartReports/components/Components/Common/LoadingErrorWrapper';
import type { Asset } from '@/types/GraphqlSchema';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '../../../types';
import type {
  ComponentDefinition,
  ComponentEditProps,
  ComponentViewProps,
  UseDataType,
} from '../types';

export const COMPONENT_ID = 'well_statistics_table';

const styles = {
  key: {
    fontFamily: (theme: Theme) => theme.fonts.inter,
    fontWeight: 'bold',
    border: 'none',
    backgroundColor: 'background.paper',
    fontSize: 'clamp(6px, 1.4vw, 12px)',
    padding: 'clamp(1px, 0.3vw, 8px)',
    lineHeight: 1,
  },
  value: {
    fontFamily: (theme: Theme) => theme.fonts.inter,
    border: 'none',
    backgroundColor: 'background.paper',
    fontSize: 'clamp(6px, 1.4vw, 12px)',
    padding: 'clamp(1px, 0.3vw, 8px)',
    lineHeight: 1,
    textAlign: 'right',
  },
  footnote: {
    fontFamily: (theme: Theme) => theme.fonts.inter,
    fontSize: '0.8em',
    marginTop: '0.5rem',
    color: (theme: Theme) => theme.palette.text.secondary,
  },
};

// GraphQL query
const QUERY = gql`
  query AssetV2($input: EntityIdentifierInput!) {
    assetV2(input: $input) {
      id
      totalWellsDrilled
      totalEaWells
      totalProductionWells
      totalInjectionWells
      totalOtherWells
      totalRemainingWells
    }
  }
`;

const useData = ({ portfolio, persistedData, onComponentLoad }: UseDataType<Asset | null>) => {
  const [dataFetched, setDataFetched] = useState(persistedData);

  // Extract asset_id from portfolio
  const assetId = portfolio?.assets?.[0];

  const { loading, error } = useQuery<{ assetV2: Asset | null }>(QUERY, {
    variables: {
      input: {
        id: assetId,
      },
    },
    skip: !assetId || !!dataFetched,
    fetchPolicy: 'network-only', // Force network request to get fresh data
    onCompleted: response => {
      if (response?.assetV2) {
        setDataFetched(response.assetV2);
        if (onComponentLoad) {
          onComponentLoad({
            componentId: COMPONENT_ID,
            data: response.assetV2,
          });
        }
      }
    },
  });

  return {
    data: dataFetched,
    error,
    loading,
  };
};

const ComponentView = ({ data }: ComponentViewProps<Asset | null | undefined>) => {
  const firstDayOfYear = new Date(new Date().getFullYear(), 0, 1).toLocaleDateString();
  const displayData = {
    [`Total Wells Drilled on Project (as of ${firstDayOfYear}):`]: data?.totalWellsDrilled ?? 0,
    'Total Exploration and Appraisal (Project Life)': data?.totalEaWells ?? 0,
    'Total Producers (Project Life)': data?.totalProductionWells ?? 0,
    'Total Injectors Wells (Project Life)': data?.totalInjectionWells ?? 0,
    'Total Other (Project Life)': data?.totalOtherWells ?? 0,
    [`Total Remaining Wells (as of ${firstDayOfYear}):`]: data?.totalRemainingWells ?? 0,
  };

  return (
    // Responsive font sizing based on container size
    <ComponentBox sx={{ fontSize: 'clamp(8px, 2vw, 14px)', justifyContent: 'space-between' }}>
      <Box display='flex' flexDirection='column' gap='0.5rem' width='100%'>
        <ComponentTitle>Well Statistics</ComponentTitle>
        <TableContainer>
          <Table size='small'>
            <TableBody>
              {Object.entries(displayData).map(([key, value]) => (
                <TableRow key={key}>
                  <TableCell sx={styles.key}>{key}</TableCell>
                  <TableCell sx={styles.value}>{value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Typography sx={styles.footnote}>
        *Well types classified per regulatory agency guidelines
      </Typography>
    </ComponentBox>
  );
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: ComponentEditProps<Asset | null>) => {
  const { data, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={data}>
      <ComponentView data={data} />
    </LoadingErrorWrapper>
  );
};

export const WellStatisticsTableComponent: ComponentDefinition<Asset | null> = {
  id: COMPONENT_ID,
  name: 'Well Statistics',
  type: SmartReportComponentTypesEnum.Table,
  reportTypes: [SmartReportTypeEnum.Asset],
  Component: ComponentEdit,
  ComponentView,
};
